import React, { useState, useEffect } from 'react';
import { Zap, X, ArrowRight, ChevronRight, Info, CheckCircle2, ShieldCheck } from 'lucide-react';

interface Props {
  className?: string;
}

export default function TaxReformNoticeBanner({ className = '' }: Props) {
  const [isDismissed, setIsDismissed] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('rmax_tax_reform_banner_v1');
    if (!dismissed) {
      setIsDismissed(false);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('rmax_tax_reform_banner_v1', 'true');
    setIsDismissed(true);
  };

  if (isDismissed) return null;

  return (
    <>
      {/* Banner Sóbrio e Confortável (Público 40/50+) */}
      <div className={`bg-white border border-emerald-200/90 rounded-2xl p-3.5 sm:p-4 shadow-xs relative overflow-hidden transition-all ${className}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-200/60 mt-0.5">
              <Zap className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-black text-slate-900 tracking-tight">
                  R-MAX 2026/2027: Preparado para a Reforma Tributária (CBS & IBS)
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                  Transição Suave
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed max-w-2xl">
                Fique tranquilo: seus cálculos de preço continuam funcionando normalmente. Em <strong>2026</strong>, a lei realiza apenas um teste técnico de 1% (sem aumento de imposto). A virada do cálculo ocorre em <strong>2027</strong> com a CBS plena.
              </p>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-800 pt-0.5 transition-colors group cursor-pointer"
              >
                <span>Entenda como o R-MAX protege o seu negócio</span>
                <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors shrink-0"
            title="Ocultar aviso"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Modal Didático e Acolhedor (Sem Alarmismo) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-slate-200 space-y-5 my-8">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-200">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 leading-tight">
                    A Reforma Tributária no seu R-MAX
                  </h3>
                  <span className="text-[11px] text-slate-500">
                    O que muda no seu bolso e como você fica protegido
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Linha do Tempo Clara e Sóbria */}
            <div className="space-y-3 text-xs">
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <div className="flex items-center justify-between font-bold text-slate-900">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    2026: Ano Piloto (Sem Aumento de Carga)
                  </span>
                  <span className="text-[10px] text-slate-500 font-semibold">Fase Técnica</span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  O governo aplica uma alíquota simbólica de 1% (0,9% CBS + 0,1% IBS) apenas para testar sistemas. Esse valor é <strong>100% compensado</strong> no seu PIS/Cofins. Seus preços não precisam mudar.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-emerald-50/70 border border-emerald-300 space-y-1">
                <div className="flex items-center justify-between font-bold text-emerald-950">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-600" />
                    2027: A Virada da CBS Federal Plena
                  </span>
                  <span className="text-[10px] text-emerald-800 font-black">Virada Oficial</span>
                </div>
                <p className="text-[11px] text-emerald-900/80 leading-relaxed">
                  PIS e COFINS são extintos definitivamente. A CBS entra em vigor calculada <strong>por fora</strong> (~8,8%). O R-MAX atualiza o cálculo automaticamente e abate o crédito dos insumos que você comprou.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <div className="flex items-center justify-between font-bold text-slate-900">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-slate-400" />
                    2028 em Diante: Transição de Estados e Municípios
                  </span>
                  <span className="text-[10px] text-slate-500 font-semibold">Gradual até 2033</span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  O ICMS e o ISS diminuem 10% ao ano enquanto o IBS sobe. O motor financeiro do R-MAX aplicará essas ponderações de forma invisível para você não ter que fazer contas de cabeça.
                </p>
              </div>
            </div>

            {/* Destaque das Proteções do R-MAX */}
            <div className="bg-slate-100/70 rounded-2xl p-3.5 space-y-2 text-[11px] text-slate-700">
              <span className="font-bold text-slate-900 block">Como o R-MAX cuida de tudo:</span>
              <ul className="space-y-1.5 list-disc list-inside text-slate-600">
                <li><strong>Custo Líquido no Estoque:</strong> Os créditos da nota de compra abatem o custo da sua receita.</li>
                <li><strong>Alíquotas com Desconto:</strong> Cesta Básica com Alíquota Zero, Serviços de Alimentação com -40% e Profissionais Liberais com -30%.</li>
                <li><strong>Venda B2B:</strong> Mostre aos seus clientes PJ quanto imposto eles podem abater, fechando propostas com facilidade.</li>
              </ul>
            </div>

            {/* Alerta Recente de Legislação: Resolução CGSN nº 194/2026 */}
            <div className="p-3.5 rounded-2xl bg-amber-50/80 border border-amber-200 text-amber-950 space-y-1">
              <div className="flex items-center gap-1.5 font-bold text-xs text-amber-900">
                <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                <span>Prazo Importante do Simples Nacional (Resolução CGSN nº 194/2026)</span>
              </div>
              <p className="text-[11px] text-amber-900/90 leading-relaxed">
                Empresas do Simples têm até <strong>30 de outubro de 2026</strong> para optar pelo recolhimento de CBS e IBS no regime regular (Simples Híbrido) a partir de 2027. No R-MAX, você já pode simular os dois cenários na tela de <em>Marca e Empresa</em> e no <em>Portal do Contador</em>.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 px-4 rounded-xl text-xs transition-colors cursor-pointer"
            >
              Entendido, voltar ao painel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
